import type { PortfolioBookKindV2, PortfolioStatementImportV2, StatementImportIntentV2,
  StatementImportOperationStatusV2, StatementImportReceiptV2 } from "@tradvera/contracts";
import type { StatementImportClientV2 } from "./client";
import { newStatementOperationIdempotencyKeyV2, statementImportWriteOutcomeUnknownV2 } from "./client";
import { finalizePendingStatementOperationV2, persistPendingStatementOperationV2,
  readPendingStatementOperationV2, samePendingStatementOperationV2,
  withPendingStatementOperationLockV2,
  type DurableStatementWriteOperationV2, type PendingStatementOperationV2 } from "./pending-operation";
import type { DurableStatementUploadStorageV2 } from "./durable-upload";

export async function executeDurableStatementWriteV2<Input, Result>(options: {
  storage: DurableStatementUploadStorageV2;
  ownerId: string;
  bookId: string;
  importId: string;
  operation: Exclude<DurableStatementWriteOperationV2, "UPLOAD">;
  targetReviewRevision?: number;
  buildInput(idempotencyKey: string): Input;
  inputFingerprint(input: Input): string;
  write(input: Input): Promise<Result>;
}): Promise<Result> {
  return withPendingStatementOperationLockV2(options.ownerId, options.bookId, async () => {
  if (options.operation === "REVIEW"
    && (!Number.isSafeInteger(options.targetReviewRevision) || Number(options.targetReviewRevision) <= 0)) {
    pendingError("REVIEW_TRUTH_TARGET_REQUIRED");
  }
  const pending = readPendingStatementOperationV2(options.storage, options.ownerId, options.bookId);
  if (pending.state === "INVALID" || pending.state === "UNAVAILABLE") pendingError(pending.state);
  if (pending.state === "VALID"
    && (pending.marker.operation !== options.operation || pending.marker.importId !== options.importId)) {
    pendingError("OTHER_OPERATION");
  }
  const idempotencyKey = pending.state === "VALID" ? pending.marker.idempotencyKey
    : newStatementOperationIdempotencyKeyV2(options.operation);
  const input = options.buildInput(idempotencyKey);
  const marker: PendingStatementOperationV2 = { operation: options.operation,
    bookId: options.bookId, importId: options.importId,
    targetReviewRevision: options.operation === "REVIEW" ? options.targetReviewRevision ?? null : null,
    idempotencyKey, inputFingerprint: options.inputFingerprint(input) };
  if (pending.state === "VALID" && !samePendingStatementOperationV2(pending.marker, marker)) {
    pendingError("INPUT_MISMATCH");
  }
  if (pending.state === "ABSENT") persistPendingStatementOperationV2(options.storage, options.ownerId, marker);
  try {
    const result = await options.write(input);
    requireFinalized(options.storage, options.ownerId, marker);
    return result;
  } catch (error) {
    if (!statementImportWriteOutcomeUnknownV2(error)) requireFinalized(options.storage, options.ownerId, marker);
    throw error;
  }
  });
}

export type StatementWriteRecoveryV2 =
  | { state: "ABSENT" }
  | { state: "RETRY_ALLOWED"; marker: PendingStatementOperationV2; status: StatementImportOperationStatusV2 }
  | { state: "PENDING"; marker: PendingStatementOperationV2; status: StatementImportOperationStatusV2 }
  | { state: "LOCKED"; code: string; marker?: PendingStatementOperationV2 }
  | { state: "RECOVERED"; marker: PendingStatementOperationV2; import: PortfolioStatementImportV2;
    status: StatementImportOperationStatusV2; intent: StatementImportIntentV2 | null;
    receipt: StatementImportReceiptV2 | null };

export async function recoverPendingStatementWriteV2(options: {
  client: Pick<StatementImportClientV2, "operationStatus" | "detail">;
  storage: DurableStatementUploadStorageV2;
  ownerId: string;
  bookId: string;
  expectedBookKind?: PortfolioBookKindV2;
  signal?: AbortSignal;
}): Promise<StatementWriteRecoveryV2> {
  return withPendingStatementOperationLockV2(options.ownerId, options.bookId, async () => {
  const pending = readPendingStatementOperationV2(options.storage, options.ownerId, options.bookId);
  if (pending.state === "ABSENT") return pending;
  if (pending.state !== "VALID") return { state: "LOCKED",
    code: `STATEMENT_IMPORT_PENDING_OPERATION_${pending.state}` };
  const marker = pending.marker;
  let imported: PortfolioStatementImportV2 | null = null;
  try {
    if (marker.importId) imported = await options.client.detail(marker.importId, options.signal);
    const status = await options.client.operationStatus({ operation: marker.operation,
      importId: marker.importId, idempotencyKey: marker.idempotencyKey,
      inputFingerprint: marker.inputFingerprint,
      dispositionCounts: imported?.reconciliation.dispositionCounts ?? null }, options.signal);
    if (status.state === "NOT_FOUND") return { state: "RETRY_ALLOWED", marker, status };
    if (status.state === "CONFLICT" || status.state === "OUTCOME_UNKNOWN") {
      return { state: "LOCKED", code: status.state, marker };
    }
    if (status.state === "PENDING" && marker.operation !== "UPLOAD") return { state: "PENDING", marker, status };
    if (status.state === "ABANDONED" && marker.operation !== "DISCARD") {
      return { state: "LOCKED", code: "STATEMENT_IMPORT_STATUS_BINDING_MISMATCH", marker };
    }
    if (!status.importId) return { state: "PENDING", marker, status };
    imported = await options.client.detail(status.importId, options.signal);
    if (!boundImport(imported, marker, status, options.expectedBookKind ?? "OWNED_PRIVATE")) {
      return { state: "LOCKED", code: "STATEMENT_IMPORT_STATUS_BINDING_MISMATCH", marker };
    }
    const intent: StatementImportIntentV2 | null = null;
    if (!operationTruthProven(imported, marker, status, intent)) {
      return { state: "LOCKED", code: "STATEMENT_IMPORT_OPERATION_TRUTH_NOT_VISIBLE", marker };
    }
    if (!finalized(options.storage, options.ownerId, marker)) {
      return replacementLock(options.storage, options.ownerId, marker);
    }
    return { state: "RECOVERED", marker, import: imported, status, intent, receipt: status.receipt };
  } catch (error) {
    return { state: "LOCKED", code: error instanceof Error && error.message
      ? error.message : "STATEMENT_IMPORT_OUTCOME_UNKNOWN", marker };
  }
  });
}

function boundImport(imported: PortfolioStatementImportV2, marker: PendingStatementOperationV2,
  status: StatementImportOperationStatusV2, expectedBookKind: PortfolioBookKindV2): boolean {
  return imported.bookId === marker.bookId && imported.bookKind === expectedBookKind
    && imported.importId === status.importId
    && (marker.importId === null || imported.importId === marker.importId);
}

function operationTruthProven(imported: PortfolioStatementImportV2, marker: PendingStatementOperationV2,
  status: StatementImportOperationStatusV2, intent: StatementImportIntentV2 | null): boolean {
  // The status event is the immutable authority for an upload operation.  A raw-SHA replay
  // legitimately reuses the same import and makes its public uploadOperation point at the
  // newest upload event, so comparing that mutable projection with an older pending marker
  // permanently locks otherwise valid daily bundle updates.
  if (marker.operation === "UPLOAD") return status.operation === "UPLOAD"
    && (status.state === "PENDING" || status.state === "COMMITTED")
    && status.importId === imported.importId;
  if (marker.operation === "REVIEW") return marker.targetReviewRevision !== null
    && imported.reviewRevision >= marker.targetReviewRevision;
  if (marker.operation === "RECONCILIATION") {
    return imported.reconciliation.inputFingerprint === marker.inputFingerprint;
  }
  if (marker.operation === "ACCEPT_INTENT") return imported.state === "INTENT_CREATED";
  if (marker.operation === "ACCEPT_CONFIRM") return status.receipt !== null
    && status.receipt.inputFingerprint === marker.inputFingerprint
    && (imported.state === "OBSERVATIONS_ACCEPTED" || imported.state === "DUPLICATE_NOOP");
  return marker.operation === "DISCARD" && imported.state === "ABANDONED" && status.state === "ABANDONED";
}

function requireFinalized(storage: DurableStatementUploadStorageV2, ownerId: string,
  marker: PendingStatementOperationV2): void {
  const result = finalizePendingStatementOperationV2(storage, ownerId, marker);
  if (result.state !== "ABSENT") pendingError(result.state === "REPLACED" ? "REPLACED" : result.state);
}

function finalized(storage: DurableStatementUploadStorageV2, ownerId: string,
  marker: PendingStatementOperationV2): boolean {
  return finalizePendingStatementOperationV2(storage, ownerId, marker).state === "ABSENT";
}

function replacementLock(storage: DurableStatementUploadStorageV2,
  ownerId: string, marker: PendingStatementOperationV2): StatementWriteRecoveryV2 {
  const current = readPendingStatementOperationV2(storage, ownerId, marker.bookId);
  return { state: "LOCKED", code: "STATEMENT_IMPORT_PENDING_OPERATION_REPLACED",
    marker: current.state === "VALID" ? current.marker : marker };
}

function pendingError(reason: string): never {
  throw new Error(`STATEMENT_IMPORT_PENDING_OPERATION_${reason}`);
}
